import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError, fetchImageObjectUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Clock, Upload, FileCheck2, Camera, UserRound } from "lucide-react";

const TYPE_LABEL = { drivers_license: "Driver's License", state_id: "State ID", passport: "Passport" };

export function IdentityVerification({ onStatusChange }) {
    const [data, setData] = useState(null);
    const [docType, setDocType] = useState("drivers_license");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoBusy, setPhotoBusy] = useState(false);

    const loadPhoto = () => fetchImageObjectUrl("/me/photo").then(setPhotoUrl);
    const load = () => api.get("/me/id-verification").then((r) => setData(r.data));
    useEffect(() => { load(); loadPhoto(); }, []);

    const uploadPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPhotoBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Profile photo saved.");
            await loadPhoto();
            window.dispatchEvent(new Event("ssc-photo-updated"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setPhotoBusy(false); e.target.value = ""; }
    };

    const submit = async () => {
        if (!file) { toast.error("Please choose a file to upload."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", docType);
            fd.append("file", file);
            await api.post("/me/id-verification", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("ID submitted. Our team will review it shortly.");
            setFile(null);
            await load();
            onStatusChange && onStatusChange();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    if (!data) return null;
    const status = data.verification_status;
    const sub = data.submission;
    const verified = status === "verified";
    const underReview = status === "needs_review" || (sub && sub.status === "submitted" && !verified);

    return (
        <div className="space-y-4" data-testid="identity-verification">
            <div className="rounded-2xl bg-card p-6 shadow-sm" data-testid="profile-photo-card">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                        <Camera className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-700 text-primary">Profile photo</h3>
                        <p className="mt-1 text-sm text-muted-foreground">Add a clear photo of yourself for your member profile card. Our matchmaking team uses this to recognise you at outings.</p>
                    </div>
                </div>
                <div className="mt-5 flex items-center gap-5">
                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-border bg-secondary/60">
                        {photoUrl ? (
                            <img src={photoUrl} alt="Your profile" className="h-full w-full object-cover" data-testid="profile-photo-preview" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-muted-foreground"><UserRound className="h-10 w-10" /></div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="profile-photo-file" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-600 text-primary-foreground hover:bg-primary/90">
                            <Upload className="h-4 w-4" /> {photoBusy ? "Uploading…" : photoUrl ? "Change photo" : "Upload photo"}
                        </label>
                        <input id="profile-photo-file" data-testid="profile-photo-input" type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic" disabled={photoBusy}
                            onChange={uploadPhoto} className="hidden" />
                        <p className="mt-2 text-xs text-muted-foreground">JPG, PNG, WEBP or HEIC · up to 10 MB</p>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl bg-card p-6 shadow-sm">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-700 text-primary">Identity Verification</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Single Social Club is for verified adults only. Upload a government photo ID so our team can
                            confirm you're a real person before you're matched into any group. Your ID is stored privately
                            and is only visible to our verification team — never to other members.
                        </p>
                    </div>
                </div>

                {verified && (
                    <div data-testid="idv-verified" className="mt-5 flex items-center gap-2 rounded-xl bg-primary/10 px-4 py-3 text-sm font-600 text-primary">
                        <ShieldCheck className="h-4 w-4" /> Your identity is verified. You're all set to be matched.
                    </div>
                )}

                {!verified && underReview && (
                    <div data-testid="idv-under-review" className="mt-5 flex items-center gap-2 rounded-xl bg-amber-100 px-4 py-3 text-sm font-600 text-amber-700">
                        <Clock className="h-4 w-4" /> We received your {sub ? TYPE_LABEL[sub.doc_type] : "ID"} and it's under review. This usually takes a day or two.
                    </div>
                )}

                {!verified && (
                    <div className="mt-5 space-y-4">
                        <div>
                            <label className="text-sm font-600 text-primary">Document type</label>
                            <Select value={docType} onValueChange={setDocType}>
                                <SelectTrigger data-testid="idv-doc-type" className="mt-2 max-w-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="drivers_license">Driver's License</SelectItem>
                                    <SelectItem value="state_id">State ID</SelectItem>
                                    <SelectItem value="passport">Passport</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-600 text-primary">Upload photo or PDF</label>
                            <label htmlFor="idv-file" className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground hover:border-primary hover:text-primary">
                                {file ? <FileCheck2 className="h-5 w-5 text-primary" /> : <Upload className="h-5 w-5" />}
                                <span className="truncate">{file ? file.name : "Choose a JPG, PNG, WEBP, HEIC or PDF (max 15 MB)"}</span>
                            </label>
                            <input id="idv-file" data-testid="idv-file-input" type="file"
                                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                                onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
                        </div>
                        <Button onClick={submit} disabled={busy} data-testid="idv-submit" className="rounded-full bg-primary hover:bg-primary/90">
                            {busy ? "Uploading…" : underReview ? "Replace document" : "Submit for verification"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
